import { useState, useEffect, useCallback } from 'react';
import { PlayerGamesPanel } from './gamePlayers/PlayerGamesPanel';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import {
    Loader2, ArrowUpRight, ArrowDownRight, Dice5, Trophy, Ticket, Coins, Activity, Crown, Receipt, Ban,
} from 'lucide-react';
import ReceiptsHistoryPanel from './ReceiptsHistoryPanel';
import { CollapsibleList } from './CollapsibleList';
import MessageCustomerButton from './admin/MessageCustomerButton';
import { PlayerName } from './PlayerDrilldownContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// Friendly labels for raw transaction_type strings
const TXN_LABEL = {
    cashier_load: 'Counter load', kiosk_cash: 'Kiosk cash-in',
    peer_transfer_in: 'Peer transfer in', peer_transfer_out: 'Peer transfer out',
    admin_credit: 'Admin credit', admin_deposit: 'Admin deposit',
    admin_withdraw: 'Admin withdraw', credit_load: 'Credits loaded',
    credit_return: 'Credits returned', credit_cashout: 'Credits cashed out',
    credit_transfer_out: 'Credits transferred', daily_bonus: 'Daily bonus',
    birthday_bonus: 'Birthday bonus', bonus: 'Bonus', referral_bonus: 'Referral bonus',
    cashier_cashout: 'Counter cashout', cashout: 'Cashout',
    cashout_request: 'Cashout request', cashout_play_back: 'Play-it-back',
    cashout_refund: 'Cashout refund',
    slot_spin: 'Slot spin', slot_wager: 'Slot wager', slot_win: 'Slot win',
    jackpot_grand: 'GRAND jackpot', jackpot_super: 'SUPER jackpot',
    lucky_pack_entry: 'Lucky pack entry', lucky_pack_payout: 'Lucky pack payout',
    lucky_pack_refund: 'Lucky pack refund',
    series_pack_entry: 'Series pack entry', series_pack_payout: 'Series pack payout',
    series_pack_refund: 'Series pack refund',
    majik_topup: 'Majik top-up', majik_redeem: 'Majik redeem',
    sports_bet: 'Sports bet',
};

const labelFor = (tt) => TXN_LABEL[tt] || (tt || '').replace(/_/g, ' ');

const PlayerActivityDialog = ({ playerId, open, onClose }) => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('all');
    const [vipSaving, setVipSaving] = useState(false);
    const [autoLoadSaving, setAutoLoadSaving] = useState(false);

    const fetchActivity = useCallback(async () => {
        if (!playerId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/cashier/players/${playerId}/activity`, {
                params: { limit: 200 }, withCredentials: true,
            });
            setData(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [playerId]);

    useEffect(() => { if (open && playerId) fetchActivity(); }, [open, playerId, fetchActivity]);

    const toggleVip = async () => {
        if (!data?.player?.id) return;
        const next = !data.player.auto_credit_vip;
        setVipSaving(true);
        try {
            await axios.post(
                `${API}/admin/users/${data.player.id}/auto-credit-vip`,
                { auto_credit_vip: next },
                { withCredentials: true }
            );
            setData((d) => d ? { ...d, player: { ...d.player, auto_credit_vip: next } } : d);
            toast.success(next
                ? `👑 ${data.player.name || 'Player'} is now VIP — auto-credit caps bypassed`
                : `VIP off — caps re-applied to ${data.player.name || 'this player'}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to flip VIP');
        } finally { setVipSaving(false); }
    };

    const toggleAutoLoad = async () => {
        if (!data?.player?.id) return;
        const nextDisabled = !data.player.auto_credit_disabled;
        setAutoLoadSaving(true);
        try {
            await axios.post(
                `${API}/admin/users/${data.player.id}/auto-credit-disabled`,
                { auto_credit_disabled: nextDisabled },
                { withCredentials: true }
            );
            setData((d) => d ? { ...d, player: { ...d.player, auto_credit_disabled: nextDisabled } } : d);
            toast.success(nextDisabled
                ? `Auto-load blocked for ${data.player.name || 'this player'} — receipts will always go to cashier review`
                : `Auto-load re-enabled for ${data.player.name || 'this player'}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to flip auto-load block');
        } finally { setAutoLoadSaving(false); }
    };
    useEffect(() => { if (!open) { setData(null); setTab('all'); } }, [open]);

    const txns = data?.transactions || [];
    const filteredTxns = tab === 'all'
        ? txns
        : txns.filter(t => t.bucket === tab);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent
                className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="player-activity-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-zinc-100">
                        <Activity className="w-5 h-5" />
                        {data?.player?.name || 'Player'} — activity
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400">
                        {data?.player?.phone || ''}
                        {data?.player?.owner_cashier_name && <> · owned by {data.player.owner_cashier_name}</>}
                        {data?.player?.status && data.player.status !== 'active' && (
                            <span className="ml-2 text-rose-300 text-xs">[{data.player.status}]</span>
                        )}
                    </DialogDescription>
                    {data?.player?.referred_by_ambassador && (
                        <div className="mt-1" data-testid="player-referred-by">
                            <div className="flex items-center gap-1.5 text-xs flex-wrap">
                                <Crown className="w-3.5 h-3.5 text-[#FFD700] flex-shrink-0" />
                                <span className="text-zinc-400">Referred by</span>
                                {data.player.referred_by_ambassador.ambassador_user_id ? (
                                    <PlayerName
                                        id={data.player.referred_by_ambassador.ambassador_user_id}
                                        className="text-[#FFD700] font-semibold"
                                        title="View this ambassador's report"
                                        testIdPrefix="referring-ambassador"
                                    >
                                        {data.player.referred_by_ambassador.ambassador_name
                                            || data.player.referred_by_ambassador.ambassador_code}
                                    </PlayerName>
                                ) : (
                                    <span className="text-[#FFD700] font-semibold">
                                        {data.player.referred_by_ambassador.ambassador_name
                                            || data.player.referred_by_ambassador.ambassador_code}
                                    </span>
                                )}
                                {data.player.referred_by_ambassador.ambassador_code && (
                                    <span className="text-zinc-500 font-mono">
                                        ({data.player.referred_by_ambassador.ambassador_code})
                                    </span>
                                )}
                                {data.player.referred_by_ambassador.is_house && (
                                    <span className="px-1.5 py-0.5 rounded bg-[#5b4a16] text-[#FFD700] text-[9px] font-bold uppercase tracking-wide"
                                        data-testid="player-house-badge">House</span>
                                )}
                            </div>
                            {(data.player.referred_by_ambassador.net_house_profit != null) && (
                                <div className="flex items-center gap-2 pl-5 mt-0.5 text-[11px] text-zinc-400">
                                    <span>Net to house <span className="text-zinc-200 font-mono">${(data.player.referred_by_ambassador.net_house_profit || 0).toFixed(2)}</span></span>
                                    <span className="text-zinc-600">·</span>
                                    <span>Earned <span className="text-emerald-400 font-mono">${(data.player.referred_by_ambassador.commission_earned || 0).toFixed(2)}</span> ({data.player.referred_by_ambassador.commission_pct || 0}%)</span>
                                </div>
                            )}
                        </div>
                    )}
                    {data?.player?.id && (
                        <div className="mt-1">
                            <MessageCustomerButton userId={data.player.id} userName={data.player.name} label="Message customer" />
                        </div>
                    )}
                </DialogHeader>

                {loading ? (
                    <div className="text-center py-12 text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading activity…
                    </div>
                ) : !data ? (
                    <p className="text-center text-zinc-500 py-8">Could not load activity.</p>
                ) : (
                    <>
                        {/* Lifetime tiles */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <Tile label="Balance" value={fmt(data.player.credits)} valueClass="text-zinc-100" />
                            <Tile label="Lifetime loaded" value={fmt(data.lifetime.loaded)} />
                            <Tile label="Lifetime cashed" value={fmt(data.lifetime.cashed)} />
                            <Tile label="Wagered" value={fmt(data.lifetime.wagered)} />
                            <Tile label="Won" value={fmt(data.lifetime.won)} />
                        </div>

                        {/* Admin-only: VIP unlimited auto-credit toggle */}
                        {isAdmin && (
                            <div
                                className={`mt-3 flex items-center justify-between gap-3 px-3 py-2 rounded-md border ${
                                    data.player.auto_credit_vip
                                        ? 'bg-[#FFD700]/10 border-[#FFD700]/40'
                                        : 'bg-zinc-900/40 border-zinc-800'
                                }`}
                                data-testid="vip-toggle-row"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <Crown className={`w-4 h-4 ${data.player.auto_credit_vip ? 'text-[#FFD700]' : 'text-zinc-500'}`} />
                                    <div className="min-w-0">
                                        <p className="text-[12px] font-bold text-zinc-100 leading-tight">
                                            VIP — unlimited auto-credit
                                            {data.player.auto_credit_vip && <span className="ml-1 text-[10px] text-[#FFD700]">ON</span>}
                                        </p>
                                        <p className="text-[10px] text-zinc-500 leading-snug">
                                            {data.player.auto_credit_vip
                                                ? 'Bypasses both the $100/receipt and $250/24h caps. Master toggle + HIGH OCR confidence + hash de-dup still apply.'
                                                : 'Flip ON to let this player auto-credit any amount with no nightly cap.'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={toggleVip}
                                    disabled={vipSaving}
                                    className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider border transition-colors ${
                                        data.player.auto_credit_vip
                                            ? 'bg-[#FFD700]/20 border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/30'
                                            : 'bg-zinc-800 border-white/10 text-zinc-400 hover:bg-zinc-700'
                                    }`}
                                    data-testid="vip-toggle-btn"
                                >
                                    {vipSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                                        : (data.player.auto_credit_vip ? 'VIP ON' : 'Make VIP')}
                                </button>
                            </div>
                        )}

                        {/* Admin-only: per-player Auto-load kill switch. Overrides VIP. */}
                        {isAdmin && (
                            <div
                                className={`mt-2 flex items-center justify-between gap-3 px-3 py-2 rounded-md border ${
                                    data.player.auto_credit_disabled
                                        ? 'bg-[#FF3B30]/10 border-[#FF3B30]/40'
                                        : 'bg-zinc-900/40 border-zinc-800'
                                }`}
                                data-testid="autoload-toggle-row"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <Ban className={`w-4 h-4 ${data.player.auto_credit_disabled ? 'text-[#FF3B30]' : 'text-zinc-500'}`} />
                                    <div className="min-w-0">
                                        <p className="text-[12px] font-bold text-zinc-100 leading-tight">
                                            Block auto-load for this player
                                            {data.player.auto_credit_disabled && <span className="ml-1 text-[10px] text-[#FF3B30]">BLOCKED</span>}
                                        </p>
                                        <p className="text-[10px] text-zinc-500 leading-snug">
                                            {data.player.auto_credit_disabled
                                                ? 'Receipts NEVER auto-credit. Always land in the cashier pending queue for manual review. Overrides VIP.'
                                                : 'Flip ON to force this player\u2019s receipts to cashier review only (no auto-credit).'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={toggleAutoLoad}
                                    disabled={autoLoadSaving}
                                    className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider border transition-colors ${
                                        data.player.auto_credit_disabled
                                            ? 'bg-[#FF3B30]/20 border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30]/30'
                                            : 'bg-zinc-800 border-white/10 text-zinc-400 hover:bg-zinc-700'
                                    }`}
                                    data-testid="autoload-toggle-btn"
                                >
                                    {autoLoadSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                                        : (data.player.auto_credit_disabled ? 'Blocked' : 'Block auto-load')}
                                </button>
                            </div>
                        )}

                        {/* Tabs */}
                        <Tabs value={tab} onValueChange={setTab} className="mt-2">
                            <TabsList className="bg-zinc-950 border border-zinc-800 h-9 grid grid-cols-8">
                                <TabsTrigger value="all" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid="tab-all">All <span className="ml-1 text-zinc-500">{txns.length}</span></TabsTrigger>
                                <TabsTrigger value="in" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid="tab-in">Ins</TabsTrigger>
                                <TabsTrigger value="out" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid="tab-out">Outs</TabsTrigger>
                                <TabsTrigger value="casino" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid="tab-casino">Slots</TabsTrigger>
                                <TabsTrigger value="sports" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid="tab-sports">Sports <span className="ml-1 text-zinc-500">{data.sports_bets?.length || 0}</span></TabsTrigger>
                                <TabsTrigger value="lottery" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid="tab-lottery">Lottery <span className="ml-1 text-zinc-500">{data.lottery_tickets?.length || 0}</span></TabsTrigger>
                                <TabsTrigger value="games" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid="tab-games">Games</TabsTrigger>
                                <TabsTrigger value="receipts" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100"
                                    data-testid="tab-receipts"><Receipt className="w-3 h-3 inline mr-0.5" />Receipts</TabsTrigger>
                            </TabsList>

                            {/* Transactions table (all/in/out/casino) */}
                            {['all', 'in', 'out', 'casino'].includes(tab) && (
                                <TabsContent value={tab} className="mt-3">
                                    {filteredTxns.length === 0 ? (
                                        <p className="text-center text-zinc-500 py-6 text-sm">No activity in this category.</p>
                                    ) : (
                                        <div className="rounded-md border border-zinc-800 overflow-hidden">
                                            <CollapsibleList
                                                items={filteredTxns}
                                                initial={5}
                                                testId={`txns-${tab}`}
                                                className="divide-y divide-zinc-800"
                                            >
                                                {(t) => <TxnRow key={t.id} t={t} />}
                                            </CollapsibleList>
                                        </div>
                                    )}
                                </TabsContent>
                            )}

                            {/* Sports bets */}
                            <TabsContent value="sports" className="mt-3">
                                {(!data.sports_bets || data.sports_bets.length === 0) ? (
                                    <p className="text-center text-zinc-500 py-6 text-sm">No sports bets.</p>
                                ) : (
                                    <div className="rounded-md border border-zinc-800 overflow-hidden">
                                        <CollapsibleList
                                            items={data.sports_bets}
                                            initial={5}
                                            testId="sports-bets"
                                            className="divide-y divide-zinc-800"
                                        >
                                            {(b) => (
                                                <div key={b.id} className="px-4 py-3 flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <p className="text-sm text-zinc-100 font-medium truncate">
                                                            {b.outcome_name || '—'}
                                                            {b.point != null && <span className="ml-1 text-zinc-500 font-mono">{b.point > 0 ? `+${b.point}` : b.point}</span>}
                                                        </p>
                                                        <p className="text-xs text-zinc-500 truncate">
                                                            {(b.home_team && b.away_team) ? `${b.away_team} @ ${b.home_team}` : (b.market || b.sport_key)}
                                                            {b.odds != null && <> · odds {b.odds > 0 ? `+${b.odds}` : b.odds}</>}
                                                        </p>
                                                        <p className="text-[10px] text-zinc-600 mt-0.5">{fmtTime(b.placed_at)}</p>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <p className="text-sm font-mono text-zinc-100">{fmt(b.amount)}</p>
                                                        <p className="text-xs font-mono text-zinc-500">→ {fmt(b.potential_payout)}</p>
                                                        <StatusPill status={b.status} />
                                                    </div>
                                                </div>
                                            )}
                                        </CollapsibleList>
                                    </div>
                                )}
                            </TabsContent>

                            {/* Lottery tickets */}
                            <TabsContent value="lottery" className="mt-3">
                                {(!data.lottery_tickets || data.lottery_tickets.length === 0) ? (
                                    <p className="text-center text-zinc-500 py-6 text-sm">No lottery tickets.</p>
                                ) : (
                                    <div className="rounded-md border border-zinc-800 overflow-hidden">
                                        <CollapsibleList
                                            items={data.lottery_tickets}
                                            initial={5}
                                            testId="lottery-tickets"
                                            className="divide-y divide-zinc-800"
                                        >
                                            {(lt) => (
                                                <div key={lt.id} className="px-4 py-3 flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <p className="text-sm text-zinc-100 font-medium">
                                                            {lt.state} {lt.pick_type}
                                                            {lt.bet_type && <span className="text-zinc-500 ml-1">({lt.bet_type})</span>}
                                                        </p>
                                                        <p className="text-xs text-zinc-400 font-mono">
                                                            {Array.isArray(lt.numbers) ? lt.numbers.join(' · ') : lt.numbers || '—'}
                                                        </p>
                                                        <p className="text-[10px] text-zinc-600 mt-0.5">
                                                            Draw {lt.draw_date}
                                                            {lt.drawing_time && <> · {lt.drawing_time}</>}
                                                        </p>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <p className="text-sm font-mono text-zinc-100">{fmt(lt.amount)}</p>
                                                        {lt.payout > 0 && <p className="text-xs font-mono text-emerald-300">+{fmt(lt.payout)}</p>}
                                                        <StatusPill status={lt.status} />
                                                    </div>
                                                </div>
                                            )}
                                        </CollapsibleList>
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="games" className="mt-3">
                                <PlayerGamesPanel userId={data.player.id} />
                            </TabsContent>

                            {/* Receipts history (per-player) */}
                            <TabsContent value="receipts" className="mt-3">
                                <ReceiptsHistoryPanel userId={data.player.id} compact />
                            </TabsContent>
                        </Tabs>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

const Tile = ({ label, value, valueClass = 'text-zinc-100' }) => (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">{label}</p>
        <p className={`text-base font-mono font-semibold ${valueClass}`}>{value}</p>
    </div>
);

const TxnRow = ({ t }) => {
    const amt = Number(t.amount) || 0;
    const positive = amt > 0;
    const Icon = (
        t.bucket === 'in' ? ArrowUpRight :
            t.bucket === 'out' ? ArrowDownRight :
                t.bucket === 'casino' ? (t.type === 'jackpot_grand' || t.type === 'jackpot_super' || t.type === 'slot_win' || t.type === 'lucky_pack_payout' ? Trophy : Dice5) :
                    t.bucket === 'sports' ? Trophy :
                        Coins
    );
    return (
        <div className="px-4 py-2.5 flex items-center gap-3" data-testid={`txn-${t.id}`}>
            <Icon className={`w-4 h-4 flex-shrink-0 ${positive ? 'text-emerald-300' : 'text-zinc-400'}`} />
            <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-100 font-medium truncate">
                    {labelFor(t.type)}
                    {t.cashier_name && <span className="text-zinc-500 font-normal"> · {t.cashier_name}</span>}
                    {t.payout_method && <span className="text-zinc-500 font-normal"> · {t.payout_method}</span>}
                </p>
                {(t.description || t.note) && (
                    <p className="text-xs text-zinc-500 truncate">{t.description || t.note}</p>
                )}
                <p className="text-[10px] text-zinc-600">{fmtTime(t.created_at)}</p>
            </div>
            <div className="text-right flex-shrink-0">
                <p className={`text-sm font-mono font-semibold ${positive ? 'text-emerald-300' : amt < 0 ? 'text-rose-300' : 'text-zinc-300'}`}>
                    {positive ? '+' : ''}{fmt(amt)}
                </p>
                <p className="text-[10px] font-mono text-zinc-500">{fmt(t.balance_after)}</p>
                {t.status && t.status !== 'paid' && <StatusPill status={t.status} />}
            </div>
        </div>
    );
};

const StatusPill = ({ status }) => {
    if (!status) return null;
    const color =
        status === 'won' || status === 'approved' || status === 'paid' ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20' :
            status === 'pending' ? 'text-amber-300 bg-amber-500/10 border-amber-500/20' :
                status === 'lost' || status === 'rejected' ? 'text-rose-300 bg-rose-500/10 border-rose-500/20' :
                    'text-zinc-400 bg-zinc-800 border-zinc-700';
    return <span className={`inline-block mt-1 text-[9px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded border ${color}`}>{status}</span>;
};

export default PlayerActivityDialog;

// Quick sanity to keep tree-shake-y bundler happy with named lucide icons
export { Ticket };
